import React from 'react';

import { formatMoney } from '@betterment/js-runtime';
import { Box, Card, Heading, ListRow, Separator, Text, Tooltip } from '@betterment/samba-react';
import { CardLoader, SambaAutoLink } from '@betterment/shared-components';

import { IraContributionYear, IraContributionYears } from './data';
import { useActiveIraContributionsAndMatchGrantsByYearQuery } from './hooks';
import IraContributionMatchSummaries from './IraContributionMatchSummaries';

import MessageBox from '../Shared/components-v2/message-box/message-box';
import { urlBuilder } from '../Shared/urlBuilder';

const enum IraMaxOutStatuses {
  NotProjectedToMax,
  ProjectedToMax,
  Maxed,
}

const IraLimitMaxOutProjection = (iraContribution: IraContributionYear): IraMaxOutStatuses => {
  const contributionLimit = iraContribution.taxYear.iraContributionLimit.cents;
  const currentContributionAmount = iraContribution.result.amount.cents;
  const projectedContributionAmount = iraContribution.result.projection?.total?.cents || 0;

  if (currentContributionAmount >= contributionLimit) {
    return IraMaxOutStatuses.Maxed;
  }
  if (projectedContributionAmount >= contributionLimit) {
    return IraMaxOutStatuses.ProjectedToMax;
  }
  return IraMaxOutStatuses.NotProjectedToMax;
};

const IraLimitTooltipCopy = (iraContribution: IraContributionYear): string => {
  if (iraContribution.taxYear.isEligibleForIraContributionCatchUp) {
    return 'Limit includes maximum standard contribution amount as well as catch-up contributions for individuals 50 and over.';
  }
  return 'Limit includes maximum standard contribution amount.';
};

const IraContributionSummaries: React.FC<{ iraContributions: IraContributionYears }> = ({ iraContributions }) => {
  return (
    <>
      {iraContributions.map((iraContribution: IraContributionYear) => {
        const status = IraLimitMaxOutProjection(iraContribution);
        const IraContributionTooltipLimitCopy = IraLimitTooltipCopy(iraContribution);
        return (
          <ListRow key={iraContribution.taxYear.id} css={{ padding: 'space.25' }}>
            <ListRow.LeadingContent css={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <Box css={{ display: 'flex', flexDirection: 'row' }}>
                <Text>{iraContribution.taxYear.year} contributions</Text>
                <Tooltip>
                  <Tooltip.Trigger asChild={true} style={{ paddingLeft: '4px' }}>
                    <Tooltip.IconButton
                      aria-label={`${iraContribution.taxYear.year} contributions tooltip`}
                      name="InfoCircle"
                    />
                  </Tooltip.Trigger>
                  <Tooltip.Content align="start" side="right">
                    {IraContributionTooltipLimitCopy}
                  </Tooltip.Content>
                </Tooltip>
              </Box>
              {status === IraMaxOutStatuses.Maxed && (
                <Text css={{ textAlign: 'left' }} color="text.success" variant="text50">
                  Maxed out
                </Text>
              )}
              {status === IraMaxOutStatuses.ProjectedToMax && (
                <Text css={{ textAlign: 'left' }} color="text.success" variant="text50">
                  Projected to max out
                </Text>
              )}
              {status === IraMaxOutStatuses.NotProjectedToMax && (
                <Text css={{ textAlign: 'left' }} color="text.weak" variant="text50">
                  Limit: {formatMoney(iraContribution.taxYear.iraContributionLimit)}
                </Text>
              )}
            </ListRow.LeadingContent>
            <ListRow.TrailingContent>
              <Text css={{ textAlign: 'right' }}>{formatMoney(iraContribution.result.amount)}</Text>
            </ListRow.TrailingContent>
          </ListRow>
        );
      })}
    </>
  );
};

const Controller: React.FC<React.PropsWithChildren> = () => {
  const { isLoading, iraContributions, iraContributionMatchGrants, messages } =
    useActiveIraContributionsAndMatchGrantsByYearQuery();

  const hasMatchGrants = Array.isArray(iraContributionMatchGrants) && iraContributionMatchGrants.length > 0;
  const hasValidMessages = messages && messages.length > 0;
  const header = hasMatchGrants ? 'IRA contributions and match' : 'IRA Contributions ';

  if (isLoading) return <CardLoader />;
  if (!iraContributions) return null;

  return (
    <Card css={{ padding: 'space.300', display: 'grid', gap: 'space.300' }}>
      <Card.Header>
        <Heading as="h3" css={{ textStyle: { base: 'title100', md: 'title200' } }}>
          {header}
        </Heading>
      </Card.Header>
      <Separator />
      <Card.Content>
        <Box css={{ display: 'grid', gap: 'space.300', marginTop: 'space.25' }}>
          <IraContributionSummaries iraContributions={iraContributions} />

          {iraContributionMatchGrants && !hasValidMessages && (
            <IraContributionMatchSummaries iraContributionMatchGrants={iraContributionMatchGrants} />
          )}
          {hasValidMessages && (
            <MessageBox messages={messages} location="goal_overview_ira_contributions">
              <MessageBox.After>
                <Separator css={{ fullWidthWithInset: 'space.25' }} />
              </MessageBox.After>
            </MessageBox>
          )}
          <Separator />
          <SambaAutoLink to={urlBuilder.appIraDetails.absolute()}>See details</SambaAutoLink>
        </Box>
      </Card.Content>
    </Card>
  );
};
export default Controller;
